import React, { useState, useEffect } from 'react';
import { getBranding, updateBranding, getInvitations } from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Palette, Users, Save } from 'lucide-react';

export const MSPSettingsPage = () => {
    const [branding, setBranding] = useState({
        company_name: '',
        logo_url: '',
        primary_color: '#3B82F6',
        accent_color: '#10B981',
        support_email: '',
    });
    const [team, setTeam] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [brandingRes, teamRes] = await Promise.all([
                getBranding().catch(() => null),
                getInvitations().catch(() => []),
            ]);
            if (brandingRes) {
                setBranding({
                    company_name: brandingRes.company_name || '',
                    logo_url: brandingRes.logo_url || '',
                    primary_color: brandingRes.primary_color || '#3B82F6',
                    accent_color: brandingRes.accent_color || '#10B981',
                    support_email: brandingRes.support_email || '',
                });
            }
            setTeam(Array.isArray(teamRes) ? teamRes : teamRes.items || []);
        } catch (err) {
            toast.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveBranding = async () => {
        setSaving(true);
        try {
            await updateBranding(branding);
            toast.success('Branding updated successfully');
        } catch (err) {
            toast.error('Failed to update branding');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64" data-testid="msp-settings-loading">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="msp-settings-page">
            {/* Page Header */}
            <div>
                <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
                    MSP Settings
                </h1>
                <p className="text-muted-foreground mt-1">
                    Configure your MSP branding and manage your team
                </p>
            </div>

            {/* Branding Section */}
            <Card className="border-border shadow-sm">
                <CardHeader>
                    <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
                        <Palette className="w-5 h-5" />
                        Branding
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-foreground">Company Name</label>
                            <Input
                                value={branding.company_name}
                                onChange={(e) => setBranding({ ...branding, company_name: e.target.value })}
                                placeholder="Your MSP Company"
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-foreground">Logo URL</label>
                            <Input
                                value={branding.logo_url}
                                onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })}
                                placeholder="https://example.com/logo.png"
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-foreground">Primary Color</label>
                            <div className="flex items-center gap-2 mt-1">
                                <Input
                                    type="color"
                                    value={branding.primary_color}
                                    onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                                    className="w-12 h-10 p-1 cursor-pointer"
                                />
                                <Input
                                    value={branding.primary_color}
                                    onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                                    placeholder="#3B82F6"
                                    className="flex-1"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-foreground">Accent Color</label>
                            <div className="flex items-center gap-2 mt-1">
                                <Input
                                    type="color"
                                    value={branding.accent_color}
                                    onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                                    className="w-12 h-10 p-1 cursor-pointer"
                                />
                                <Input
                                    value={branding.accent_color}
                                    onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                                    placeholder="#10B981"
                                    className="flex-1"
                                />
                            </div>
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-sm font-medium text-foreground">Support Email</label>
                            <Input
                                type="email"
                                value={branding.support_email}
                                onChange={(e) => setBranding({ ...branding, support_email: e.target.value })}
                                placeholder="support@yourmsp.com"
                                className="mt-1"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end pt-2">
                        <Button onClick={handleSaveBranding} disabled={saving}>
                            <Save className="w-4 h-4 mr-2" />
                            {saving ? 'Saving...' : 'Save Branding'}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Team Management Section */}
            <Card className="border-border shadow-sm">
                <CardHeader>
                    <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        Team Management
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {team.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">No team members found.</p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Role</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {team.map((member) => (
                                    <TableRow key={member.id || member.email}>
                                        <TableCell className="font-medium">{member.name || member.invited_name || '-'}</TableCell>
                                        <TableCell>{member.email || member.invited_email}</TableCell>
                                        <TableCell>
                                            <Badge variant="secondary" className="text-xs">
                                                {member.role || 'member'}
                                            </Badge>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default MSPSettingsPage;
